import {Form, Formik, FormikHelpers} from "formik";
import Layout from "../../components/Layout";
import {
    capitalize,
    Chip,
    DialogActions,
    DialogContent,
    Paper,
    Typography,
} from "@mui/material";
import * as yup from "yup";
import React, {useCallback} from "react";
import {useNavigate, useParams} from "react-router";
import SubmitButton from "../../components/SubmitButton";
import {useTranslation} from "react-i18next";
import {useUpdateMeineAbwesenheit} from "../../client/hooks";
import {useMutation, useQueryClient} from "react-query";
import {AbwesenheitDaten} from "../../api";
import AbwesenheitenFieldsContent from "../../components/abwesenheiten/AbwesenheitenFieldsContent";
import {useAbwesenheit} from "../../client/queries";
import ErrorAlert from "../../components/ErrorAlert";
import {notPermitted} from "../../errors";
import Guard from "../../components/Guard";
import ShowFormikErrors from "../../components/allgemein/ShowFormikErrors";
import DeleteAbwesenheitButton from "../../components/abwesenheiten/DeleteAbwesenheitButton";
import DeleteIcon from "@mui/icons-material/Delete";

export default function DetailsMeineAbwesenheit() {
    const {t: translation} = useTranslation("customer");
    const {id: abwesenheitId = ""} = useParams<{
        id: string;
    }>();
    const abwesenheit = useAbwesenheit(abwesenheitId);
    const {mutateAsync} = useSaveAbwesenheit(abwesenheitId);

    let schema = yup.object().shape({
        start: yup
            .date()
            .required("Datum ist erforderlich!")
            .max(
                new Date("9999-12-31"),
                "Datum darf nicht über dem 31.12.9999 liegen!"
            ),
        ende: yup
            .date()
            .required("Datum ist erforderlich!")
            .max(
                new Date("9999-12-31"),
                "Datum darf nicht über dem 31.12.9999 liegen!"
            ),
        mitarbeiterId: yup.string(),
        bemerkung: yup.string(),
        type: yup.string(),
        brzKey: yup
            .string()
            .nullable() // Erlaubt null, da das Feld optional ist
            .when("type", {
                is: "Arbeitszeitkonto",
                then: (schema) => schema.notRequired(),
                otherwise: (schema) =>
                    schema.required("BRZ-Schlüssel ist erforderlich!"),
            }),
    });
    const navigate = useNavigate();

    const onSubmit = useCallback(
        async (
            values: {
                start: string;
                ende: string;
                mitarbeiterId: string;
                bemerkung: string;
                type: string;
                brzKey: string;
            },
            formikHelpers: FormikHelpers<{
                start: string;
                ende: string;
                mitarbeiterId: string;
                bemerkung: string;
                type: string;
                brzKey: string;
            }>
        ) => {
            const {setSubmitting, setStatus} = formikHelpers;
            if (values.start && values.ende) {
                await mutateAsync({
                    mitarbeiterId: values.mitarbeiterId ?? "",
                    start: values.start,
                    ende: values.ende,
                    type: values.type ?? "urlaub",
                    bemerkung: values.bemerkung,
                    ...(values.brzKey !== "" && {brzKey: values.brzKey}),
                });
                navigate(`/meine-abwesenheit`);
            }
            setStatus(undefined);
            try {
            } catch (error: any) {
                setStatus(error.message);
            } finally {
                setSubmitting(false);
            }
        },
        [navigate, mutateAsync]
    );
    return (
        <Layout title={"Abwesenheit"} onBackClick={() => navigate(-1)}>
            <Guard
                permission={"meine:abwesenheit:edit"}
                fallback={<ErrorAlert error={notPermitted()}/>}
            >
                <Paper
                    elevation={1}
                    sx={{
                        marginTop: 2,
                        padding: 3,
                        marginLeft: 2,
                        marginRight: 2,
                        position: "relative",
                    }}
                >
                    {abwesenheit.status !== "ANGENOMMEN" && (
                        <DeleteAbwesenheitButton
                            id={abwesenheitId}
                        ></DeleteAbwesenheitButton>
                    )}
                    <Formik
                        initialValues={{
                            start: abwesenheit.start ?? "",
                            ende: abwesenheit.ende ?? "",
                            mitarbeiterId: abwesenheit.mitarbeiterId ?? "",
                            bemerkung: abwesenheit.bemerkung ?? "",
                            type: abwesenheit.type ?? "",
                            brzKey: abwesenheit.brzKey ?? "",
                        }}
                        validationSchema={schema}
                        onSubmit={onSubmit}
                    >
                        {({
                              isSubmitting,
                              isValidating,
                              isValid,
                              dirty,
                              errors,
                              validateForm,
                          }) => (
                            <>
                                <Typography variant="h5">
                                    {abwesenheit.geloeschtAm === undefined ? (
                                        <>{abwesenheit.type}</>
                                    ) : (
                                        <>
                                            <>{abwesenheit.type}</>
                                            <Chip
                                                size="small"
                                                icon={<DeleteIcon/>}
                                                label={"gelöscht"}
                                                color="error"
                                            />
                                        </>
                                    )}
                                </Typography>
                                <DialogContent>
                                    <Form id="new-abwesenheit">
                                        <AbwesenheitenFieldsContent
                                            isVerwaltung={false}
                                            isNew={false}
                                        />
                                    </Form>
                                </DialogContent>
                                <DialogActions>
                                    <ShowFormikErrors errors={errors}>
                                        <SubmitButton
                                            form="new-abwesenheit"
                                            type="submit"
                                            variant="contained"
                                            color="secondary"
                                            loading={isSubmitting}
                                            disabled={isValidating || !isValid || !dirty}
                                            onMouseEnter={() => validateForm()}
                                        >
                                            {capitalize(translation("save"))}
                                        </SubmitButton>
                                    </ShowFormikErrors>
                                </DialogActions>
                            </>
                        )}
                    </Formik>
                </Paper>
            </Guard>
        </Layout>
    );
}

function useSaveAbwesenheit(id: string) {
    const updateAbwesenheit = useUpdateMeineAbwesenheit();
    const queryClient = useQueryClient();

    return useMutation(
        (input: AbwesenheitDaten) => {
            return updateAbwesenheit(id, input);
        },
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(["abwesenheiten"]);
            },
        }
    );
}
